import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, ArrowLeft, Printer, FileText } from 'lucide-react';
import { orderService } from '../api/orders';
import { encounterService } from '../api/encounters';
import { patientService } from '../../../lib/services';
import { useStore } from '../../../lib/store';
import { can, getUserRole } from '../../../lib/rbac';
import { generateLabReportPDF } from '../../../lib/pdf';
import toast from 'react-hot-toast';
import type { Order } from '../types';
import type { Patient } from '../../../lib/supabase';
import type { Encounter } from '../types';

const resultSchema = z.object({
  results: z.array(z.object({
    itemCode: z.string(),
    itemName: z.string(),
    resultValue: z.string().min(1, 'Result value is required'),
    units: z.string().optional(),
    referenceRange: z.string().optional(),
    flag: z.enum(['H', 'L', 'N']).optional(),
    notes: z.string().optional(),
  }))
});

interface OrderResult {
  itemCode: string;
  itemName: string;
  resultValue: string;
  units?: string;
  referenceRange?: string;
  flag?: 'H' | 'L' | 'N';
  notes?: string;
  technician?: string;
  reportedAt?: string;
}

type ResultFormData = z.infer<typeof resultSchema>;

interface OrderWithDetails extends Order {
  encounter?: Encounter;
  patient?: Patient;
  invoiceId?: string;
  results?: OrderResult[];
}

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useStore();
  const [order, setOrder] = useState<OrderWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<ResultFormData>({
    resolver: zodResolver(resultSchema),
    defaultValues: {
      results: []
    }
  });

  const { fields } = useFieldArray({
    control,
    name: 'results',
  });

  useEffect(() => {
    if (id) {
      loadOrder();
    }
  }, [id]);

  const loadOrder = async () => {
    try {
      const orderData = await orderService.getById(id!);
      const encounter = await encounterService.getById(orderData.encounterId);
      const patient = await patientService.getById(encounter.patientId);
      
      const orderWithDetails: OrderWithDetails = {
        ...orderData,
        encounter,
        patient,
      };
      
      setOrder(orderWithDetails);
      
      const initialResults = (orderData as any).results || orderData.items.map(item => ({
        itemCode: item.code || '',
        itemName: item.name,
        resultValue: '',
        units: '',
        referenceRange: '',
        flag: 'N' as const,
        notes: '',
      }));
      
      setValue('results', initialResults);
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ResultFormData) => {
    if (!order) return;
    
    setSaving(true);
    try {
      const updatedOrder = await (orderService as any).updateResults(order.id, data.results);
      setOrder({ ...order, ...updatedOrder });
      toast.success('Results saved successfully');
    } catch (error) {
      toast.error('Failed to save results');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!order) return;
    
    try {
      const updatedOrder = await orderService.updateStatus(order.id, 'completed');
      setOrder({ ...order, ...updatedOrder });
      toast.success('Order marked as completed');
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handlePrintReport = () => {
    if (!order) return;
    generateLabReportPDF(order, order.patient!, order.encounter!);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Order not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
            <p className="text-gray-600">Order #{order.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {order.invoiceId && (
            <Link
              to={`/billing/invoices/${order.invoiceId}`}
              className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 flex items-center space-x-2"
            >
              <FileText className="w-4 h-4" />
              <span>View Invoice</span>
            </Link>
          )}
          {order.status === 'completed' && can(getUserRole(user), 'print_lab_reports' as any) && (
            <button
              onClick={handlePrintReport}
              className="px-4 py-2 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 flex items-center space-x-2"
            >
              <Printer className="w-4 h-4" />
              <span>Print Report</span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Order Information</h3>
            <dl className="space-y-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="text-sm text-gray-900 capitalize">{order.type}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Priority</dt>
                <dd className="text-sm text-gray-900 capitalize">{order.priority}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className={`text-sm font-medium capitalize ${
                  order.status === 'completed' ? 'text-green-600' :
                  (order.status as any) === 'processing' ? 'text-blue-600' :
                  (order.status as any) === 'collected' ? 'text-purple-600' :
                  'text-yellow-600'
                }`}>
                  {order.status}
                </dd>
              </div>
            </dl>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Patient Information</h3>
            <dl className="space-y-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="text-sm text-gray-900">{order.patient?.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="text-sm text-gray-900">{order.patient?.email}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {can(getUserRole(user), 'enter_lab_results' as any) && order.status !== 'pending' && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Test Results</h3>
          
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{field.itemName}</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Result Value</label>
                    <input
                      {...register(`results.${index}.resultValue`)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {errors.results?.[index]?.resultValue && (
                      <p className="mt-1 text-sm text-red-600">{errors.results[index]?.resultValue?.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Units</label>
                    <input
                      {...register(`results.${index}.units`)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference Range</label>
                    <input
                      {...register(`results.${index}.referenceRange`)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Flag</label>
                    <select
                      {...register(`results.${index}.flag`)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="N">Normal</option>
                      <option value="H">High</option>
                      <option value="L">Low</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    {...register(`results.${index}.notes`)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-6">
            <div>
              {order.status === 'processing' && can(getUserRole(user), 'manage_order_status' as any) && (
                <button
                  type="button"
                  onClick={handleMarkCompleted}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                >
                  <span>Mark Completed</span>
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Results'}</span>
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
